import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Container, IconButton, InputAdornment, Link, TextField, Toolbar, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { APP_NAME, ROUTES } from '@/config/app.config';
import { ApiError } from '@/infrastructure/services/api-client';
import { useAuthStore } from '@/stores/use-auth-store';
import { useNotificationStore } from '@/stores/use-notification-store';
import { useThemeStore } from '@/stores/use-theme-store';

export function LoginPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { login } = useAuthStore();
    const { addNotification } = useNotificationStore();
    const { mode } = useThemeStore();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            await login(email, password);
            addNotification(t('notifications.welcomeEmail'), 'success');
            navigate(ROUTES.dashboard);
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            } else {
                addNotification(t('errors.invalidCredentials'), 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                background: mode === 'dark'
                    ? 'linear-gradient(180deg, #11132B 0%, #1A1C3E 100%)'
                    : 'linear-gradient(180deg, #FAFBFC 0%, #F0F2F5 100%)',
            }}
        >
            <Container maxWidth="sm">
                <Toolbar />
                <Card
                    sx={{
                        border: '1px solid',
                        borderColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontWeight: 800,
                                    color: 'primary.main',
                                    mb: 1,
                                }}
                            >
                                {APP_NAME}
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {t('auth.login.title')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('auth.login.subtitle')}
                            </Typography>
                        </Box>

                        <Box component="form" onSubmit={handleSubmit}>
                            <TextField
                                fullWidth
                                label={t('auth.login.email')}
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label={t('auth.login.password')}
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                sx={{ mb: 3 }}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                size="large"
                                disabled={loading}
                                sx={{ py: 1.5, fontWeight: 700, borderRadius: 3 }}
                            >
                                {loading ? t('common.loading') : t('auth.login.submit')}
                            </Button>
                        </Box>

                        <Box sx={{ textAlign: 'center', mt: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                {t('auth.login.noAccount')}{' '}
                                <Link
                                    component="button"
                                    onClick={() => navigate(ROUTES.register)}
                                    sx={{ fontWeight: 600 }}
                                >
                                    {t('common.signUp')}
                                </Link>
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
}

export default LoginPage;
